import React, { memo, useMemo } from 'react';
import { useAppSelector, useAppStore } from 'hooks/redux';
import { List as ImmutableList, Record } from 'immutable';

import { categoriesTypes } from 'app-components/flux/categories';
import { tracker } from 'app-components/utils/core';
import type { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { mkTag } from 'app-components/flux/tags/tagsTypes';
import { getProjectById } from 'app-components/flux/projects/projectsSelectors';
import { splitsHelpers, transactionsSelectors } from 'app-components/flux/transactions';
import { splitItemsToArrayOverflowLast } from 'app-components/flux/transactions/splitsHelpers';
import type { CashFlowTransaction, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import { type ProjectSelect } from 'components/ClientProjectSelect';
import MuiTagsField from 'components/QuickenControls/TagsField/TagsField';
import TransactionSplits from 'components/TransactionSplits';
import UsageWithCategory from 'components/UsageWithCategory';

import { makeStyles } from 'tss-react/mui';
import { AI_HIGHLIGHT_TEXTFIELD_SX } from './aiHighlightSx';

type TagsFieldOnChange = NonNullable<React.ComponentProps<typeof MuiTagsField>['onChange']>;

const useStyles = makeStyles()((theme) => ({
  categorySplitsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(3),
    width: '100%',
  },
  stackedRow: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  thinRow: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(3),
    '.MuiFormControl-root': {
      margin: 0,
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 260,
  },
}));

function CategorySection(props: {
  accountId: string;
  coa: chartOfAccountsTypes.ChartOfAccountProps | null | undefined;
  setCoa: (newCOA: chartOfAccountsTypes.ChartOfAccountProps) => void;
  txn: CashFlowTransaction;
  setTags: (tag: TransactionProps['tags']) => void;
  amount: number;
  validPayee: boolean;
  businessId: string | null | undefined;
  setBusinessId: (businessIdParam: string | undefined) => void;
  customersAndProjects: ProjectSelect;
  resetCustomersAndProjects: () => void;
  onOpenSplitDialog: () => void;
  goToOtherSide: (txn: TransactionProps) => void;
  showSplitRow: boolean | undefined;
  splitsDisabled: boolean;
  disabled: boolean | undefined;
  tagsDisabled?: boolean;
  /** Don't allow user to select ar account as transfer cat. */
  disabledPayment: boolean;
  /** Drawer layout stacks Category above Tags (full width) instead of the side-by-side modal row. */
  drawerLayout?: boolean;
  /** Drawer: content rendered between the category and tags (e.g. the transfer "other side" link). */
  belowCategoryContent?: React.ReactNode;
  /** Highlights the category field, e.g. right after an AI suggestion updates it. */
  categoryHighlighted?: boolean;
  /** Highlights the tags field, e.g. right after an AI suggestion updates it. */
  tagsHighlighted?: boolean;
}) {
  const { classes, cx } = useStyles();
  const {
    accountId,
    coa,
    setCoa,
    setTags,
    amount,
    businessId,
    setBusinessId,
    onOpenSplitDialog,
    txn,
    showSplitRow,
    splitsDisabled,
    disabled = false,
    tagsDisabled = disabled,
    goToOtherSide,
    disabledPayment,
    customersAndProjects,
    resetCustomersAndProjects,
    drawerLayout = false,
    belowCategoryContent,
    categoryHighlighted,
    tagsHighlighted,
  } = props;

  const store = useAppStore();

  const splitItems = splitItemsToArrayOverflowLast(txn.split?.items || []);
  const hasSplitsAssigned = splitItems.length > 0;

  const transactionsById = useAppSelector(transactionsSelectors.getResourcesById);
  const isOtherSide = useMemo(
    () => splitsHelpers.isSplitTransferCounterpart({ txn, transactionsById }),
    [txn, transactionsById],
  );
  const showSplitActions = !isOtherSide && (Boolean(showSplitRow) || hasSplitsAssigned);

  const updateUsageAndCategory = ({
    usage: newUsage,
    coa: newCoa,
  }: {
    usage: string;
    coa: chartOfAccountsTypes.ChartOfAccountProps;
  }) => {
    setCoa(newCoa);
    setBusinessId(newUsage !== 'PERSONAL' ? newUsage : '');

    // If project is selected but new usage does not match, reset project field. Also reset if new usage is personal.
    if (customersAndProjects.projectId || newUsage === 'PERSONAL') {
      const projectBiz = getProjectById(store.getState(), customersAndProjects.projectId ?? '')?.businessId;
      if (projectBiz !== newUsage || newUsage === 'PERSONAL') {
        resetCustomersAndProjects();
      }
    }
  };

  const initSplit = () => {
    onOpenSplitDialog();
    tracker.track(tracker.events.viewSplitsV2, {
      initiator: txn.id === null ? 'createTxn' : 'editTxn',
      transactionAmount: txn.amount,
      transactionAmountSign: txn.amount >= 0 ? 'positive' : 'negative',
    });
  };

  const tagsValue = Array.isArray(txn.tags) ? txn.tags : txn.tags?.toArray() || [];
  const updateTags: TagsFieldOnChange = (_event, tagArray) => {
    setTags(ImmutableList(tagArray.map((tag) => (Record.isRecord(tag) ? tag : mkTag(tag)))));
  };

  // # ===================================================================================== #
  // # -----                               Main Render                                 ----- #
  // # ===================================================================================== #

  return (
    <div id='category-splits-section' className={classes.categorySplitsSection}>
      {!hasSplitsAssigned && (
        <div className={cx(classes.row, drawerLayout && classes.stackedRow)}>
          <div className={classes.column}>
            <UsageWithCategory
              accountId={accountId}
              categoryProps={{
                margin: 'normal',
                textFieldVariant: 'filled',
                recommendedCategoryType:
                  amount > 0 ? categoriesTypes.CategoryTypeEnum.INCOME : categoriesTypes.CategoryTypeEnum.EXPENSE,
                variant: 'body1',
                label: 'Category',
                editable: true,
                name: 'txn-details',
                id: 'cat-field',
                value: coa,
                classes: { root: classes.column },
                createEnabled: true,
                textFieldProps: categoryHighlighted ? { sx: AI_HIGHLIGHT_TEXTFIELD_SX } : undefined,
              }}
              disabledPayment={disabledPayment}
              onChange={updateUsageAndCategory}
              disabled={disabled}
              usageProps={{
                value: businessId,
              }}
            />
          </div>

          {belowCategoryContent}

          <MuiTagsField
            value={tagsValue}
            onChange={updateTags}
            disabled={tagsDisabled}
            slotProps={tagsHighlighted ? { textFieldProps: { sx: AI_HIGHLIGHT_TEXTFIELD_SX } } : undefined}
          />
        </div>
      )}

      {showSplitActions ? (
        <div className={classes.thinRow}>
          <TransactionSplits
            transaction={txn}
            onAssignSplits={initSplit}
            buttonLabel={drawerLayout ? 'Split transactions' : 'Split'}
            buttonSupportingText='Use to assign multiple tags or categories to this series'
            drawerLayout={drawerLayout}
            goToOtherSide={goToOtherSide}
            disabledSplitsEdit={splitsDisabled}
            viewWhenDisabled={splitsDisabled}
          />
        </div>
      ) : null}
    </div>
  );
}

export default memo(CategorySection);
